import React, { useRef, useState, useEffect, useMemo, useLayoutEffect, useCallback } from "react";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Component, ChartConfig } from "@/types";
import {
  getChartSizeConfig,
  getChartResponsiveConfig,
  getBreakpointChartConfig,
  type BreakpointChartConfig,
  ResponsiveChartConfigInput,
} from "@/utils/chart-responsive-utils";
import {
  useQueryTemplateData,
  isQueryTemplateDataSource,
  getXAxisKeyFromQuery,
  getLineKeysFromQuery,
  getXAxisKeyFromComposite,
  getLineKeysFromComposite,
} from "@/hooks/useQueryTemplateData";
import { humanizeFieldName } from "@/utils";
import { formatAxisTick } from "@/utils/PropertyFormatters";
import { ChartDataSource, ComputedField, isCompositeQueryDataSource } from "@/types/chartDataSource";
import { enrichDataWithComputedFields } from "@/utils/computedFields";
import { resolveColorArray } from "@/utils/cssVariableResolver";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useChartHoverStore } from "@/stores/chart/chart-hover-store";
import { useChartNavigation } from "@/hooks/useChartNavigation";
import { useComponentInteraction } from "@/components/shared/rendering/InteractionContextRuntime";
import { NavigationIndicator } from "./NavigationIndicator";
import { useComponentData } from "@/stores/editor/component-data-store";
import { computeTightNumericDomain, filterSeriesKeysWithNumericData, normalizeChartRows, sanitizeNumericDomain, sortChartRowsByXAxis } from "./chartDataUtils";
import { extractTimeZoneFromRecord, findRawTemporalField, getBrowserTimeZone, isTemporalFieldName, resolvePreferredTimeZone } from "@/utils/dateTimeFormatting";
import { ChartLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";

interface LineChartProps {
  component: Component;
  isPreview?: boolean;
}

const defaultData = [
  { name: "Jan", value: 400, value2: 240 },
  { name: "Feb", value: 300, value2: 139 },
  { name: "Mar", value: 500, value2: 980 },
  { name: "Apr", value: 280, value2: 390 },
  { name: "May", value: 590, value2: 480 },
  { name: "Jun", value: 320, value2: 380 },
];

const defaultColors = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#8dd1e1",
  "#d084d0",
];

const defaultTimeSeriesColors = [
  "var(--color-timeseries-1)",
  "var(--color-timeseries-2)",
  "var(--color-timeseries-3)",
  "var(--color-timeseries-4)",
  "var(--color-timeseries-5)",
  "var(--color-timeseries-6)",
  "var(--color-timeseries-7)",
  "var(--color-timeseries-8)",
];

type ChartRow = Record<string, unknown>;

type ChartMouseMoveState = {
  activePayload?: Array<{ payload?: ChartRow }>;
  activeTooltipIndex?: number | string | null;
};

type LineClickPayload = {
  payload?: ChartRow;
  index?: number | string;
};

type LegendFormatterValue = {
  name?: unknown;
  key?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isLineClickPayload = (value: unknown): value is LineClickPayload => {
  return typeof value === "object" && value !== null;
};

const toOptionalNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const getOptionalString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const getTitleValue = (value: unknown): string | undefined => {
  const title = getOptionalString(value);
  return title === undefined || title === "" ? undefined : title;
};

const isChartDataSource = (value: unknown): value is ChartDataSource =>
  isRecord(value) && typeof value.type === "string";

const isLineChartDebugEnabled = (): boolean =>
  typeof window !== "undefined" &&
  window.localStorage.getItem("terramatrix_chart_debug") === "true";

const getStringArray = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every(item => typeof item === "string")
    ? value
    : null;

const isComputedField = (value: unknown): value is ComputedField =>
  isRecord(value) &&
  typeof value.name === "string" &&
  typeof value.expression === "string";

const getComputedFields = (value: unknown): ComputedField[] | undefined =>
  Array.isArray(value) && value.every(isComputedField) ? value : undefined;

const isLineCurveType = (value: unknown): value is LineCurveType => {
  switch (value) {
    case "basis":
    case "basisClosed":
    case "basisOpen":
    case "bumpX":
    case "bumpY":
    case "bump":
    case "linear":
    case "linearClosed":
    case "natural":
    case "monotoneX":
    case "monotoneY":
    case "monotone":
    case "step":
    case "stepBefore":
    case "stepAfter":
      return true;
    default:
      return false;
  }
};

const formatLegendValue = (value: unknown): React.ReactNode => {
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (isRecord(value)) {
    const legendValue: LegendFormatterValue = value;
    const displayValue = legendValue.name ?? legendValue.key;
    if (typeof displayValue === "string" || typeof displayValue === "number") {
      return displayValue;
    }
  }

  return String(value);
};

type LineCurveType =
  | "basis"
  | "basisClosed"
  | "basisOpen"
  | "bumpX"
  | "bumpY"
  | "bump"
  | "linear"
  | "linearClosed"
  | "natural"
  | "monotoneX"
  | "monotoneY"
  | "monotone"
  | "step"
  | "stepBefore"
  | "stepAfter";

const resolveLineCurveType = (value: unknown): LineCurveType =>
  isLineCurveType(value)
    ? value
    : "monotone";

const getLinePalette = (colors: unknown, seriesCount: number): string[] => {
  if (!Array.isArray(colors) || !colors.every((color) => typeof color === "string")) {
    return defaultTimeSeriesColors;
  }

  return seriesCount > 1 && colors.length < seriesCount ? defaultTimeSeriesColors : colors;
};

const getOptionalMargin = (value: unknown): ChartConfig["margin"] | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const top = toOptionalNumber(value.top);
  const right = toOptionalNumber(value.right);
  const bottom = toOptionalNumber(value.bottom);
  const left = toOptionalNumber(value.left);
  const margin = {
    ...(top !== undefined ? { top } : {}),
    ...(right !== undefined ? { right } : {}),
    ...(bottom !== undefined ? { bottom } : {}),
    ...(left !== undefined ? { left } : {}),
  };

  return Object.keys(margin).length > 0 ? margin : undefined;
};

const isBreakpointChartConfig = (value: unknown): value is BreakpointChartConfig => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    (value.showLegend === undefined || typeof value.showLegend === "boolean") &&
    (value.showGrid === undefined || typeof value.showGrid === "boolean") &&
    (value.showTooltip === undefined || typeof value.showTooltip === "boolean") &&
    (value.showTooltipContent === undefined || typeof value.showTooltipContent === "boolean") &&
    (value.showXAxis === undefined || typeof value.showXAxis === "boolean") &&
    (value.showYAxis === undefined || typeof value.showYAxis === "boolean") &&
    (value.showDots === undefined || typeof value.showDots === "boolean") &&
    (value.strokeWidth === undefined || toOptionalNumber(value.strokeWidth) !== undefined) &&
    (value.dotSize === undefined || toOptionalNumber(value.dotSize) !== undefined) &&
    (value.axisFontSize === undefined || toOptionalNumber(value.axisFontSize) !== undefined) &&
    (value.legendFontSize === undefined || toOptionalNumber(value.legendFontSize) !== undefined) &&
    (value.margin === undefined || getOptionalMargin(value.margin) !== undefined || (isRecord(value.margin) && Object.keys(value.margin).length === 0)) &&
    (value.title === undefined || value.title === null || typeof value.title === "string") &&
    (value.strokeDasharray === undefined || typeof value.strokeDasharray === "string") &&
    (value.strokeOpacity === undefined || toOptionalNumber(value.strokeOpacity) !== undefined) &&
    (value.lineType === undefined || typeof value.lineType === "string") &&
    (value.style === undefined || isRecord(value.style))
  );
};

const getResponsiveChartConfigInput = (value: unknown): ResponsiveChartConfigInput | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const config: ResponsiveChartConfigInput = {};

  if (isBreakpointChartConfig(value.mobile)) {
    config.mobile = value.mobile;
  }
  if (isBreakpointChartConfig(value.tablet)) {
    config.tablet = value.tablet;
  }
  if (isBreakpointChartConfig(value.desktop)) {
    config.desktop = value.desktop;
  }

  return Object.keys(config).length > 0 ? config : undefined;
};

export const LineChart: React.FC<LineChartProps> = ({
  component,
  isPreview = false,
}) => {
  const { properties = {}, style = {}, size } = component;
  const containerRef = useRef<HTMLDivElement>(null);

  // Phase 2: Track container width for responsive breakpoint detection
  const [containerWidth, setContainerWidth] = useState(400);

  // Track palette changes via CSS variable mutations
  const [paletteChangeCounter, setPaletteChangeCounter] = useState(0);

  // Get dataSource configuration
  const dataSource = isChartDataSource(properties.dataSource) ? properties.dataSource : undefined;
  const templateId = getOptionalString(properties.queryTemplateId);
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);
  const hasCompositeQuery = isCompositeQueryDataSource(dataSource);

  // Execute Query Template if configured
  const {
    data: queryTemplateData,
    isLoading: qtLoading,
    error: qtError,
    refetch: qtRefetch,
    lastFetched,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate || hasCompositeQuery || !!templateId,
    templateId,
    // Don't seed with static template data — it's placeholder-scale and causes wrong Y-axis
    // until live data arrives. Charts show empty during loading instead.
  });

  // Component data store (populated by fetch-schema-data interactions)
  const { data: storeData, loading: storeLoading, error: storeError } = useComponentData(component.id);

  // Loading/error state: merge store + query template
  const loading = storeLoading || qtLoading;
  const error = storeError || qtError;

  // Data priority: Query template > Store data > Static data > Default data
  // When a live query is configured but still loading, use [] rather than stale static data —
  // static data in the template often has placeholder values at a completely different scale.
  const hasLiveQuery = hasQueryTemplate || hasCompositeQuery || !!templateId;
  const chartData = useMemo(() => {
    const hasLiveQueryTemplateData = hasLiveQuery && Array.isArray(queryTemplateData);

    if (hasLiveQueryTemplateData) {
      return queryTemplateData;
    }

    if (storeData.length > 0) {
      return storeData;
    }

    if (hasLiveQuery) {
      return queryTemplateData || [];
    }

    return properties.data && Array.isArray(properties.data) ? properties.data : defaultData;
  }, [hasLiveQuery, properties.data, queryTemplateData, storeData]);

  const chartRows = useMemo(
    () => chartData.filter(isRecord),
    [chartData]
  );

  const normalizedChartData = useMemo(
    () => normalizeChartRows(chartRows),
    [chartRows]
  );

  const computedFields = useMemo(
    () => getComputedFields(properties.computedFields),
    [properties.computedFields]
  );

  // Enrich data with computed fields (derived columns like "profit = revenue - cost")
  const enrichedData = useMemo(
    () => enrichDataWithComputedFields(normalizedChartData, computedFields),
    [computedFields, normalizedChartData]
  );

  const sanitizedEnrichedData = useMemo(
    () => normalizeChartRows(enrichedData),
    [enrichedData]
  );

  // Determine axis keys - use query definition if available, validate against actual data, or auto-detect
  const queryXKey = hasCompositeQuery
    ? getXAxisKeyFromComposite(dataSource.compositeQueryDefinition)
    : hasQueryTemplate && dataSource?.queryDefinition
      ? getXAxisKeyFromQuery(dataSource.queryDefinition)
      : null;
  const autoXKey = normalizedChartData.length > 0
    ? Object.keys(normalizedChartData[0]).find(k => typeof normalizedChartData[0][k] === 'string' || k.includes('date') || k.includes('time'))
    : null;
  // Only use the explicit xAxisKey if it actually exists in the data — otherwise fall through to
  // queryXKey / autoXKey so stale template field names don't prevent auto-detection.
  const explicitXKey = getOptionalString(properties.xAxisKey);
  const explicitXKeyValid = explicitXKey && (normalizedChartData.length === 0 || explicitXKey in normalizedChartData[0]);
  const initialXAxisKey = (explicitXKeyValid ? explicitXKey : null)
    || (queryXKey && normalizedChartData.length > 0 && queryXKey in normalizedChartData[0] ? queryXKey : null)
    || autoXKey
    || queryXKey
    || "name";

  const effectiveXAxisKey = useMemo(() => {
    const firstRow = normalizedChartData[0];
    const rawTemporalKey = isTemporalFieldName(initialXAxisKey)
      ? findRawTemporalField(firstRow, initialXAxisKey)
      : undefined;
    return rawTemporalKey || initialXAxisKey;
  }, [initialXAxisKey, normalizedChartData]);

  const sortedChartData = useMemo(
    () => sortChartRowsByXAxis(sanitizedEnrichedData, effectiveXAxisKey),
    [effectiveXAxisKey, sanitizedEnrichedData]
  );

  // Write enriched data to chart hover store for linked DataText idle display.
  // Also publish under _templateNodeId so DataText components placed from older
  // template instantiations (where linkedChartId still holds the template string
  // ID) can find the data without needing UUID resolution.
  const setChartData = useChartHoverStore((state) => state.setChartData);
  const templateNodeId = getOptionalString(properties._templateNodeId);
  useEffect(() => {
    if (sortedChartData && sortedChartData.length > 0) {
      setChartData(component.id, sortedChartData);
      if (templateNodeId) setChartData(templateNodeId, sortedChartData);
    }
  }, [sortedChartData, component.id, templateNodeId, setChartData]);

  const queryLineKeys = hasCompositeQuery
    ? getLineKeysFromComposite(dataSource.compositeQueryDefinition)
    : hasQueryTemplate && dataSource?.queryDefinition
      ? getLineKeysFromQuery(dataSource.queryDefinition)
      : null;
  const validatedLineKeys = queryLineKeys && normalizedChartData.length > 0
    ? queryLineKeys.filter(k => normalizedChartData.some(row => k in row))
    : queryLineKeys;
  // Validate explicit keys against actual data — fall through if they don't match
  const explicitLineKeys = getStringArray(properties.lineKeys);
  const validatedExplicitLineKeys = explicitLineKeys && normalizedChartData.length > 0
    ? explicitLineKeys.filter(k => normalizedChartData.some(row => k in row))
    : explicitLineKeys;
  const numericKeysFromData = normalizedChartData.length > 0
    ? [...new Set(normalizedChartData.flatMap(row => Object.keys(row)))]
        .filter(key => key !== effectiveXAxisKey && normalizedChartData.some(row => {
          const value = row[key];
          return typeof value === 'number' && Number.isFinite(value);
        }))
    : null;
  const dataDerivedLineKeys = hasLiveQuery &&
    numericKeysFromData &&
    numericKeysFromData.length > (validatedExplicitLineKeys?.length ?? 0)
    ? numericKeysFromData
    : null;
  // For composite-query sources, prefer the source-defined aliases over explicit
  // lineKeys. AI-generated components often carry a stale one-series lineKeys
  // array even when the composite definition projects multiple metrics.
  const detectedLineKeys = (hasCompositeQuery && validatedLineKeys && validatedLineKeys.length > 0 ? validatedLineKeys : null)
    || dataDerivedLineKeys
    || (validatedExplicitLineKeys && validatedExplicitLineKeys.length > 0 ? validatedExplicitLineKeys : null)
    || (!hasCompositeQuery && validatedLineKeys && validatedLineKeys.length > 0 ? validatedLineKeys : null)
    || numericKeysFromData;

  // Inject computed field names into keys (don't rely on auto-detection — null values fail typeof check)
  const computedFieldNames = useMemo(
    () => computedFields?.map(f => f.name) || [],
    [computedFields]
  );
  const effectiveLineKeys = useMemo(() => {
    if (detectedLineKeys) {
      return [...detectedLineKeys, ...computedFieldNames.filter(name => !detectedLineKeys.includes(name))];
    }

    return computedFieldNames.length > 0 ? computedFieldNames : null;
  }, [computedFieldNames, detectedLineKeys]);

  // Get size configuration from actual rendered width (via ResizeObserver) for accurate responsive behavior
  const sizeConfig = getChartSizeConfig(containerWidth, toOptionalNumber(size?.height));
  const { isSmall } = sizeConfig;

  // Get responsive configuration
  const title = getTitleValue(properties.title);
  const description = getOptionalString(properties.description);
  const userMargin = getOptionalMargin(properties.margin);

  // Note: This config is computed for backwards compatibility but effectiveResponsiveConfig is used
  const _responsiveConfig = getChartResponsiveConfig(
    sizeConfig,
    !!title,
    !!description,
    userMargin,
    properties.showLegend !== false,
    properties.showGrid !== false
  );

  // Watch for CSS variable changes on ancestor with data-palette-scope
  useLayoutEffect(() => {
    const findPaletteScope = (element: HTMLElement | null): HTMLElement | null => {
      while (element) {
        if (element.dataset.paletteScope === 'true') return element;
        element = element.parentElement;
      }
      return null;
    };

    const paletteScope = findPaletteScope(containerRef.current);
    if (!paletteScope) return;

    // Watch for style attribute changes (PaletteScope updates CSS variables via style.setProperty)
    const observer = new MutationObserver(() => {
      setPaletteChangeCounter(c => c + 1);
    });

    observer.observe(paletteScope, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, []);

  // Phase 2: Track container width for responsive breakpoint detection
  // IMPORTANT: Use offsetWidth instead of getBoundingClientRect().width
  // because the editor canvas applies CSS transforms (scale) which affects
  // getBoundingClientRect but not offsetWidth. We want the actual layout
  // size, not the visually scaled size, for breakpoint detection.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      // Use offsetWidth for transform-independent measurement
      const width = container.offsetWidth;
      setContainerWidth(width);
    };

    // Initial measurement
    updateWidth();

    // Watch for size changes
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Phase 2: Get breakpoint-specific configuration
  const responsiveChartConfig = getResponsiveChartConfigInput(properties.responsiveChartConfig);
  const breakpointConfig = getBreakpointChartConfig(containerWidth, responsiveChartConfig);

  // Phase 2: Apply breakpoint overrides to properties
  const effectiveProperties: Record<string, unknown> = breakpointConfig ? {
    ...properties,
    ...(breakpointConfig.showLegend !== undefined && { showLegend: breakpointConfig.showLegend }),
    ...(breakpointConfig.showGrid !== undefined && { showGrid: breakpointConfig.showGrid }),
    ...(breakpointConfig.showTooltip !== undefined && { showTooltip: breakpointConfig.showTooltip }),
    ...(breakpointConfig.showTooltipContent !== undefined && { showTooltipContent: breakpointConfig.showTooltipContent }),
    ...(breakpointConfig.showXAxis !== undefined && { showXAxis: breakpointConfig.showXAxis }),
    ...(breakpointConfig.showYAxis !== undefined && { showYAxis: breakpointConfig.showYAxis }),
    ...(breakpointConfig.showDots !== undefined && { showDots: breakpointConfig.showDots }),
    ...(breakpointConfig.strokeWidth !== undefined && { strokeWidth: breakpointConfig.strokeWidth }),
    ...(breakpointConfig.dotSize !== undefined && { dotSize: breakpointConfig.dotSize }),
    ...(breakpointConfig.axisFontSize !== undefined && { axisFontSize: breakpointConfig.axisFontSize }),
    ...(breakpointConfig.legendFontSize !== undefined && { legendFontSize: breakpointConfig.legendFontSize }),
    ...(breakpointConfig.margin !== undefined && { margin: breakpointConfig.margin }),
    ...(breakpointConfig.title !== undefined && { title: breakpointConfig.title }),
    ...(breakpointConfig.strokeDasharray !== undefined && { strokeDasharray: breakpointConfig.strokeDasharray }),
    ...(breakpointConfig.strokeOpacity !== undefined && { strokeOpacity: breakpointConfig.strokeOpacity }),
    ...(breakpointConfig.lineType !== undefined && { lineType: breakpointConfig.lineType }),
  } : properties;

  // Phase 2: Use effectiveProperties for all chart config (with breakpoint overrides applied)
  const effectiveTitle = getTitleValue(effectiveProperties.title);
  const effectiveMargin = getOptionalMargin(effectiveProperties.margin);

  // Phase 2: Compute responsive config using effective properties (with breakpoint overrides)
  const effectiveResponsiveConfig = getChartResponsiveConfig(
    sizeConfig,
    !!effectiveTitle,
    !!description,
    effectiveMargin,
    effectiveProperties.showLegend !== false,
    effectiveProperties.showGrid !== false
  );

  // Phase 2: Apply breakpoint-specific axis font size and legend font size overrides
  const effectiveAxisFontSize = toOptionalNumber(effectiveProperties.axisFontSize) ?? effectiveResponsiveConfig.axisFontSize;
  const effectiveLegendFontSize = toOptionalNumber(effectiveProperties.legendFontSize) ?? effectiveResponsiveConfig.legendFontSize;

  // Dynamic margin adjustment: when Y-axis is hidden, set right margin to 0 for edge-to-edge
  // When Y-axis is showing, use default right margin for visual balance
  const baseMargin = effectiveResponsiveConfig.margin || { top: 10, right: 20, bottom: 20, left: 20 };
  const adjustedMargin = {
    ...baseMargin,
    right: effectiveProperties.showYAxis === false ? 0 : (baseMargin.right ?? 20),
  };

  const effectiveYAxisKey = getOptionalString(effectiveProperties.yAxisKey) || "value";

  const lineKeys = useMemo(
    () => filterSeriesKeysWithNumericData(
      sortedChartData,
      effectiveLineKeys || [effectiveYAxisKey]
    ),
    [effectiveLineKeys, effectiveYAxisKey, sortedChartData]
  );

  // Resolve CSS variables in colors to actual color values for Recharts
  const [resolvedColors, setResolvedColors] = useState<string[]>(() =>
    getLinePalette(effectiveProperties.colors, lineKeys.length)
  );

  // Resolve colors after DOM is mounted to ensure palette scope CSS variables are accessible.
  // Re-resolve when palette changes to update CSS variable values.
  useLayoutEffect(() => {
    const colors = getLinePalette(effectiveProperties.colors, lineKeys.length);
    const resolved = resolveColorArray(colors, containerRef.current);
    setResolvedColors(resolved);
  }, [effectiveProperties.colors, component.id, lineKeys.length, paletteChangeCounter]);

  const chartConfig: ChartConfig = {
    title: effectiveTitle,
    data: sortedChartData,
    xAxisKey: effectiveXAxisKey,
    yAxisKey: effectiveYAxisKey,
    showLegend: effectiveResponsiveConfig.showLegend,
    showTooltip: effectiveProperties.showTooltip !== false,
    showGrid: effectiveResponsiveConfig.showGrid,
    colors: resolvedColors,
    responsive: effectiveProperties.responsive !== false,
    animation: effectiveProperties.animation !== false,
    margin: adjustedMargin,
  };
  const debugEnabled = isLineChartDebugEnabled();
  const rowKeyUnion = useMemo(
    () => [...new Set(normalizedChartData.flatMap(row => Object.keys(row)))],
    [normalizedChartData]
  );

  useEffect(() => {
    if (!debugEnabled) return;

    const compositeDef = dataSource?.compositeQueryDefinition;
    console.info('[LineChart debug]', {
      componentId: component.id,
      name: component.name,
      title,
      templateId,
      dataSourceType: dataSource?.type,
      hasCompositeQuery,
      hasQueryTemplate,
      rowCount: normalizedChartData.length,
      firstRowKeys: Object.keys(normalizedChartData[0] ?? {}),
      rowKeyUnion,
      explicitLineKeys,
      queryLineKeys,
      validatedLineKeys,
      validatedExplicitLineKeys,
      numericKeysFromData,
      dataDerivedLineKeys,
      effectiveLineKeys,
      renderedLineKeys: lineKeys,
      xAxisKey: effectiveXAxisKey,
      compositeFieldAliases: compositeDef?.fields?.map(field => field.alias),
      compositeSourceMeasureAliases: compositeDef?.sources?.flatMap(source =>
        source.query.measures.map(measure => measure.alias || measure.field)
      ),
    });
  }, [
    component.id,
    component.name,
    dataDerivedLineKeys,
    dataSource?.type,
    dataSource?.compositeQueryDefinition,
    debugEnabled,
    effectiveLineKeys,
    effectiveXAxisKey,
    explicitLineKeys,
    hasCompositeQuery,
    hasQueryTemplate,
    lineKeys,
    normalizedChartData,
    numericKeysFromData,
    queryLineKeys,
    rowKeyUnion,
    templateId,
    title,
    validatedExplicitLineKeys,
    validatedLineKeys,
  ]);

  const explicitYAxisDomain = sanitizeNumericDomain(effectiveProperties.yDomain);
  const yAxisDomain = useMemo(() => {
    if (explicitYAxisDomain || lineKeys.length === 0) {
      return explicitYAxisDomain;
    }

    return computeTightNumericDomain(sortedChartData, lineKeys);
  }, [explicitYAxisDomain, lineKeys, sortedChartData]);

  const displayTimeZone = useMemo(
    () => resolvePreferredTimeZone(
      getOptionalString(effectiveProperties.timeZone),
      getOptionalString(effectiveProperties.timezone),
      extractTimeZoneFromRecord(
        isRecord(dataSource?.queryDefinition)
          ? dataSource.queryDefinition
          : undefined
      ),
      extractTimeZoneFromRecord(sortedChartData[sortedChartData.length - 1]),
      getBrowserTimeZone()
    ),
    [dataSource?.queryDefinition, effectiveProperties.timeZone, effectiveProperties.timezone, sortedChartData]
  );

  // NOTE: Recharts' tickFormatter receives (value, tickIndex) where tickIndex is the
  // index within the *rendered* ticks array, NOT the data array index. When Recharts
  // auto-culls ticks to prevent overlap, tick index N may correspond to data point M
  // where M >> N. Using sortedChartData[tickIndex] would look up the wrong row.
  // Since effectiveXAxisKey already resolves to the raw temporal field, `value` is
  // already the correct timestamp — just format it directly.
  const xAxisTickFormatter = useCallback(
    (value: unknown) => {
      return formatAxisTick(value, { timeZone: displayTimeZone });
    },
    [displayTimeZone]
  );

  const tooltipLabelFormatter = useCallback(
    (label: unknown, payload: Array<{ payload?: ChartRow }> | undefined) => {
      const row = payload?.[0]?.payload ?? {};
      const rawTemporalKey = isTemporalFieldName(initialXAxisKey)
        ? findRawTemporalField(row, initialXAxisKey)
        : undefined;
      const labelToFormat = rawTemporalKey ? row?.[rawTemporalKey] : label;
      const rowTimeZone = extractTimeZoneFromRecord(row);
      return formatAxisTick(labelToFormat, { timeZone: rowTimeZone || displayTimeZone });
    },
    [displayTimeZone, initialXAxisKey]
  );

  // Chart hover store handlers for compound chart integration
  const setHoverData = useChartHoverStore((state) => state.setHoverData);
  const clearHoverData = useChartHoverStore((state) => state.clearHoverData);

  // Chart navigation hooks for click-through navigation
  const interaction = useComponentInteraction(component);
  const { handleChartClick, isNavigable, getNavigationForElement } = useChartNavigation(component, interaction);

  const handleChartMouseMove = useCallback(
    (e: ChartMouseMoveState | undefined) => {
      if (e?.activePayload?.[0]?.payload) {
        const payload = e.activePayload[0].payload;
        const index = toOptionalNumber(e.activeTooltipIndex) ?? null;
        setHoverData(component.id, payload, index);
        if (templateNodeId) setHoverData(templateNodeId, payload, index);
      }
    },
    [component.id, templateNodeId, setHoverData]
  );

  const handleChartMouseLeave = useCallback(() => {
    clearHoverData(component.id);
    if (templateNodeId) clearHoverData(templateNodeId);
  }, [component.id, templateNodeId, clearHoverData]);

  const handleLineClick = useCallback(
    (key: string) => (data: unknown) => {
      const payload = isLineClickPayload(data) ? data.payload ?? {} : {};
      const index = isLineClickPayload(data) ? toOptionalNumber(data.index) : undefined;
      void handleChartClick(payload, key, index);
    },
    [handleChartClick]
  );

  // Calculate minimum height for the chart area
  const dynamicMinimum = sizeConfig.isVerySmall ? 40 : sizeConfig.isSmall ? 70 : 90;

  // Container style - let it fill the wrapper
  // Strip width-related properties from component.style - sizing is handled by ComponentWrapper/FlexContainer
  const visualStyle = Object.fromEntries(
    Object.entries(style).filter(
      ([key, value]) =>
        key !== "width" &&
        key !== "minWidth" &&
        key !== "maxWidth" &&
        (typeof value === "string" || typeof value === "number")
    )
  );
  const containerStyle = {
    ...visualStyle,
    position: 'relative',
    height: '100%',
    width: '100%',
    minHeight: (typeof effectiveProperties.minHeight === 'string' || typeof effectiveProperties.minHeight === 'number')
      ? effectiveProperties.minHeight
      : `${dynamicMinimum + (effectiveResponsiveConfig.showTitle ? 40 : 0)}px`,
    display: 'flex',
    flexDirection: 'column',
  } satisfies React.CSSProperties;

  return (
    <div
      className="w-full h-full"
      style={containerStyle}
      ref={containerRef}
      data-tmx-line-chart-debug={debugEnabled ? "true" : undefined}
      data-tmx-line-chart-component-id={debugEnabled ? component.id : undefined}
      data-tmx-line-chart-title={debugEnabled ? title : undefined}
      data-tmx-line-chart-line-keys={debugEnabled ? lineKeys.join(",") : undefined}
      data-tmx-line-chart-row-keys={debugEnabled ? rowKeyUnion.join(",") : undefined}
      data-tmx-line-chart-data-source={debugEnabled ? dataSource?.type : undefined}
      data-tmx-line-chart-template-id={debugEnabled ? templateId : undefined}
    >
      <NavigationIndicator isNavigable={isNavigable && !isPreview} />
      {effectiveResponsiveConfig.showTitle && effectiveProperties.showInternalTitle !== false && (
        <div className={`text-center ${effectiveResponsiveConfig.titleMargin} relative`}>
          <h3 className={effectiveResponsiveConfig.titleClass}>{chartConfig.title}</h3>
          {effectiveResponsiveConfig.showDescription && (
            <p className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
          {/* Refresh button for query template data sources */}
          {hasQueryTemplate && !isPreview && (
            <button
              onClick={() => qtRefetch()}
              className="absolute right-2 top-0 p-1 rounded hover:bg-muted/50 transition-colors"
              title={lastFetched ? `Last updated: ${lastFetched.toLocaleTimeString()}` : 'Refresh data'}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      )}

      <div style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
        {loading ? (
          <ChartLoadingSkeleton variant="line" />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : normalizedChartData && normalizedChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsLineChart
              key={yAxisDomain ? `${Math.round(yAxisDomain[0])}-${Math.round(yAxisDomain[1])}-${lineKeys.join(',')}` : `${component.id}-auto`}
              data={chartConfig.data}
              margin={chartConfig.margin}
              style={{ userSelect: isPreview ? "auto" : "none" }}
              onMouseMove={handleChartMouseMove}
              onMouseLeave={handleChartMouseLeave}
            >
            {chartConfig.showGrid && effectiveProperties.showXAxis !== false && effectiveProperties.showYAxis !== false && (
              <CartesianGrid strokeDasharray="3 3" />
            )}
            <XAxis
              dataKey={chartConfig.xAxisKey}
              tick={effectiveProperties.showXAxis === false ? false : { fontSize: effectiveAxisFontSize }}
              tickFormatter={xAxisTickFormatter}
              axisLine={effectiveProperties.showXAxis !== false}
              tickLine={effectiveProperties.showXAxis !== false}
              padding={{ left: 10, right: 10 }}
              label={effectiveProperties.xAxisLabel && effectiveProperties.showXAxis !== false && !isSmall ? { value: String(effectiveProperties.xAxisLabel), position: 'insideBottom', offset: -5, fontSize: effectiveAxisFontSize } : undefined}
            />
            <YAxis
              hide={effectiveProperties.showYAxis === false}
              width={effectiveResponsiveConfig.yAxisWidth}
              tick={effectiveProperties.showYAxis === false ? false : { fontSize: effectiveAxisFontSize }}
              axisLine={effectiveProperties.showYAxis !== false}
              tickLine={effectiveProperties.showYAxis !== false}
              domain={yAxisDomain}
              label={effectiveProperties.yAxisLabel && effectiveProperties.showYAxis !== false && !isSmall ? { value: String(effectiveProperties.yAxisLabel), angle: -90, position: 'insideLeft', fontSize: effectiveAxisFontSize } : undefined}
            />
            {chartConfig.showTooltip && (
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "6px",
                }}
                content={effectiveProperties.showTooltipContent === false ? () => null : undefined}
                labelFormatter={tooltipLabelFormatter}
                cursor={{ stroke: 'hsl(var(--border))', strokeWidth: 1 }}
              />
            )}
            {chartConfig.showLegend && (
              <Legend
                wrapperStyle={{ fontSize: `${effectiveLegendFontSize}px` }}
                formatter={formatLegendValue}
              />
            )}
            {lineKeys.map((key: string, index: number) => {
              const lineColor = chartConfig.colors?.[index % chartConfig.colors.length] || defaultColors[index % defaultColors.length];
              const dotSize = toOptionalNumber(effectiveProperties.dotSize) ?? 4;
              const dotColor = getOptionalString(effectiveProperties.dotColor) ?? lineColor;
              const dotFill = getOptionalString(effectiveProperties.dotFill) ?? lineColor;
              const dotStrokeWidth = toOptionalNumber(effectiveProperties.dotStrokeWidth) ?? 1;

              // Create dot configuration based on effective properties (with breakpoint overrides)
              const dotConfig = effectiveProperties.showDots !== false ? {
                r: dotSize,
                fill: dotFill,
                stroke: dotColor,
                strokeWidth: dotStrokeWidth,
              } : false;

              // Determine if this series has navigation configured
              const seriesNavigation = getNavigationForElement(key);
              const hasSeriesNavigation = isNavigable && seriesNavigation;

              return (
                <Line
                  key={key}
                  type={resolveLineCurveType(effectiveProperties.lineType)}
                  dataKey={key}
                  name={humanizeFieldName(key)}
                  stroke={lineColor}
                  strokeWidth={toOptionalNumber(effectiveProperties.strokeWidth) ?? 2}
                  strokeDasharray={getOptionalString(effectiveProperties.strokeDasharray)}
                  strokeOpacity={toOptionalNumber(effectiveProperties.strokeOpacity) ?? 1}
                  dot={dotConfig}
                  animationDuration={chartConfig.animation ? 300 : 0}
                  connectNulls={effectiveProperties.connectNulls !== false}
                  onClick={handleLineClick(key)}
                  cursor={hasSeriesNavigation ? 'pointer' : 'default'}
                />
              );
            })}
          </RechartsLineChart>
        </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">No chart data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
